/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetAuthzDirectoryUserPathParameters,
  ListAuthzDirectoryUsersParams,
  TLAuthzUserDirectoryEntry,
  TLHTTPValidationError,
  TLPaginatedResponseAuthzUserDirectoryEntry,
  TLUserCreateRequest,
  TLUserRepresentation,
  TLUserUpdateStatusRequest,
  UpdateUserEnabledPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Create a Keycloak user (admin-only) and return the created user.
 * @summary Create User
 */
export const createUser = (
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserRepresentation>(
    {
      url: `/api/v1/authz/users`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUserCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateUserQueryKey = (tLUserCreateRequest?: BodyType<TLUserCreateRequest>) => {
  return ['POST', 'api', 'v1', 'authz', 'users', tLUserCreateRequest] as const;
};

export const getCreateUserQueryOptions = <
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateUserQueryKey(tLUserCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createUser>>> = ({ signal }) =>
    createUser(tLUserCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateUserQueryResult = NonNullable<Awaited<ReturnType<typeof createUser>>>;
export type CreateUserQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateUser<
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createUser>>,
          TError,
          Awaited<ReturnType<typeof createUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateUser<
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createUser>>,
          TError,
          Awaited<ReturnType<typeof createUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateUser<
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create User
 */

export function useCreateUser<
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateUserQueryOptions(tLUserCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create User
 */
export const prefetchCreateUserQuery = async <
  TData = Awaited<ReturnType<typeof createUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateUserQueryOptions(tLUserCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create User
 */
export const invalidateCreateUser = async (
  queryClient: QueryClient,
  tLUserCreateRequest: BodyType<TLUserCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateUserQueryKey(tLUserCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary List Authz Directory Users
 */
export const listAuthzDirectoryUsers = (
  params?: ListAuthzDirectoryUsersParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseAuthzUserDirectoryEntry>(
    { url: `/api/v1/authz/users/directory`, method: 'GET', params, signal },
    options,
  );
};

export const getListAuthzDirectoryUsersQueryKey = (params?: ListAuthzDirectoryUsersParams) => {
  return ['api', 'v1', 'authz', 'users', 'directory', ...(params ? [params] : [])] as const;
};

export const getListAuthzDirectoryUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAuthzDirectoryUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>> = ({ signal }) =>
    listAuthzDirectoryUsers(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAuthzDirectoryUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>>;
export type ListAuthzDirectoryUsersQueryError = ErrorType<TLHTTPValidationError>;

export function useListAuthzDirectoryUsers<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListAuthzDirectoryUsersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
          TError,
          Awaited<ReturnType<typeof listAuthzDirectoryUsers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAuthzDirectoryUsers<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
          TError,
          Awaited<ReturnType<typeof listAuthzDirectoryUsers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAuthzDirectoryUsers<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Authz Directory Users
 */

export function useListAuthzDirectoryUsers<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAuthzDirectoryUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Authz Directory Users
 */
export const prefetchListAuthzDirectoryUsersQuery = async <
  TData = Awaited<ReturnType<typeof listAuthzDirectoryUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListAuthzDirectoryUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListAuthzDirectoryUsersQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Authz Directory Users
 */
export const invalidateListAuthzDirectoryUsers = async (
  queryClient: QueryClient,
  params?: ListAuthzDirectoryUsersParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListAuthzDirectoryUsersQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Authz Directory User
 */
export const getAuthzDirectoryUser = (
  { email }: GetAuthzDirectoryUserPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAuthzUserDirectoryEntry>(
    { url: `/api/v1/authz/users/${email}`, method: 'GET', signal },
    options,
  );
};

export const getGetAuthzDirectoryUserQueryKey = ({ email }: GetAuthzDirectoryUserPathParameters) => {
  return ['api', 'v1', 'authz', 'users', email] as const;
};

export const getGetAuthzDirectoryUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetAuthzDirectoryUserPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAuthzDirectoryUserQueryKey({ email });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthzDirectoryUser>>> = ({ signal }) =>
    getAuthzDirectoryUser({ email }, requestOptions, signal);

  return { queryKey, queryFn, enabled: email !== null && email !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAuthzDirectoryUserQueryResult = NonNullable<Awaited<ReturnType<typeof getAuthzDirectoryUser>>>;
export type GetAuthzDirectoryUserQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAuthzDirectoryUser<
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAuthzDirectoryUserPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
          TError,
          Awaited<ReturnType<typeof getAuthzDirectoryUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAuthzDirectoryUser<
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAuthzDirectoryUserPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
          TError,
          Awaited<ReturnType<typeof getAuthzDirectoryUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAuthzDirectoryUser<
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAuthzDirectoryUserPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Authz Directory User
 */

export function useGetAuthzDirectoryUser<
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetAuthzDirectoryUserPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAuthzDirectoryUserQueryOptions({ email }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Authz Directory User
 */
export const prefetchGetAuthzDirectoryUserQuery = async <
  TData = Awaited<ReturnType<typeof getAuthzDirectoryUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { email }: GetAuthzDirectoryUserPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAuthzDirectoryUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAuthzDirectoryUserQueryOptions({ email }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Authz Directory User
 */
export const invalidateGetAuthzDirectoryUser = async (
  queryClient: QueryClient,
  { email }: GetAuthzDirectoryUserPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAuthzDirectoryUserQueryKey({ email }) }, options);

  return queryClient;
};

/**
 * Update a Keycloak user's enabled status (admin-only).
 * @summary Update User Enabled
 */
export const updateUserEnabled = (
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserRepresentation>(
    {
      url: `/api/v1/authz/users/${email}/status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUserUpdateStatusRequest,
      signal,
    },
    options,
  );
};

export const getUpdateUserEnabledQueryKey = (
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest?: BodyType<TLUserUpdateStatusRequest>,
) => {
  return ['PATCH', 'api', 'v1', 'authz', 'users', email, 'status', tLUserUpdateStatusRequest] as const;
};

export const getUpdateUserEnabledQueryOptions = <
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateUserEnabledQueryKey({ email }, tLUserUpdateStatusRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateUserEnabled>>> = ({ signal }) =>
    updateUserEnabled({ email }, tLUserUpdateStatusRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: email !== null && email !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateUserEnabled>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateUserEnabledQueryResult = NonNullable<Awaited<ReturnType<typeof updateUserEnabled>>>;
export type UpdateUserEnabledQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateUserEnabled<
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateUserEnabled>>,
          TError,
          Awaited<ReturnType<typeof updateUserEnabled>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateUserEnabled<
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateUserEnabled>>,
          TError,
          Awaited<ReturnType<typeof updateUserEnabled>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateUserEnabled<
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update User Enabled
 */

export function useUpdateUserEnabled<
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateUserEnabledQueryOptions({ email }, tLUserUpdateStatusRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update User Enabled
 */
export const prefetchUpdateUserEnabledQuery = async <
  TData = Awaited<ReturnType<typeof updateUserEnabled>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateUserEnabled>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateUserEnabledQueryOptions({ email }, tLUserUpdateStatusRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update User Enabled
 */
export const invalidateUpdateUserEnabled = async (
  queryClient: QueryClient,
  { email }: UpdateUserEnabledPathParameters,
  tLUserUpdateStatusRequest: BodyType<TLUserUpdateStatusRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateUserEnabledQueryKey({ email }, tLUserUpdateStatusRequest) },
    options,
  );

  return queryClient;
};
